import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "dark" | "light";

const STORAGE_KEY = "astria-theme";
const THEME_EVENT = "astria-theme-change";

export const getStoredTheme = (): ThemeMode => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
};

// Components whose colors live in JS (e.g. Life GPS tokens) register here so
// they switch palette before the next render.
const listeners = new Set<(mode: ThemeMode) => void>();
export const onThemeApply = (cb: (mode: ThemeMode) => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

export const getAppliedTheme = (): ThemeMode =>
  document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";

export const applyTheme = (mode: ThemeMode) => {
  if (getAppliedTheme() === mode) return;
  document.documentElement.setAttribute("data-theme", mode);
  listeners.forEach((cb) => cb(mode));
};

export const useTheme = () => {
  const [theme, setThemeState] = useState<ThemeMode>(getStoredTheme);

  useEffect(() => {
    const sync = () => setThemeState(getStoredTheme());
    window.addEventListener(THEME_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(THEME_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const toggleTheme = useCallback(() => {
    const next: ThemeMode = getStoredTheme() === "dark" ? "light" : "dark";
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable: still switch for this session */
    }
    applyTheme(next);
    setThemeState(next);
    window.dispatchEvent(new Event(THEME_EVENT));
  }, []);

  return { theme, toggleTheme };
};
